// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { TifUtils } from '@shared/utils/Trading/TifUtils';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { TIF } from '@shared/utils/Trading/OrderTif';
import { OrderTif } from '@shared/utils/Trading/OrderTifEnum';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { TradingUtils } from '@shared/utils/Trading/TradingUtils';
import { UUID } from '@shared/utils/UUID';
import { SessionSettings } from '../../../../SessionSettings';
import { PaperPosition } from './PaperPosition';
export class PaperPositionsCache {
    constructor() {
        this._paperPositions = [];
    }
    getPaperPositions() {
        return [...this._paperPositions];
    }
    getPaperPosition(option) {
        if (isNullOrUndefined(option)) {
            return undefined;
        }
        for (let i = 0; i < this._paperPositions.length; i++) {
            const paperPosition = this._paperPositions[i];
            if (paperPosition.isPosition()) {
                continue;
            }
            if (paperPosition.Instrument === option) {
                return paperPosition;
            }
        }
        return undefined;
    }
    getPaperPositionsForAnalyzer() {
        const paperPositions = [];
        for (let i = 0; i < this._paperPositions.length; i++) {
            const paperPosition = this._paperPositions[i];
            if (!paperPosition.Analyse) {
                continue;
            }
            paperPositions.push(paperPosition);
        }
        return paperPositions;
    }
    getPaperPositionForOrder(order) {
        if (isNullOrUndefined(order)) {
            return undefined;
        }
        for (let i = 0; i < this._paperPositions.length; i++) {
            const paperPosition = this._paperPositions[i];
            if (isValidString(order.Comment) &&
                order.Comment.startsWith('SavedOrder.') &&
                order.Comment.substring('SavedOrder.'.length) === paperPosition.Id) {
                return paperPosition;
            }
        }
        return undefined;
    }
    getPaperPositionForPosition(position) {
        if (isNullOrUndefined(position)) {
            return undefined;
        }
        for (let i = 0; i < this._paperPositions.length; i++) {
            const paperPosition = this._paperPositions[i];
            if (paperPosition.isAttachedPosition(position)) {
                return paperPosition;
            }
            if (isValidString(position.Comment) &&
                position.Comment.startsWith('SavedOrder.') &&
                position.Comment.substring('SavedOrder.'.length) === paperPosition.Id) {
                return paperPosition;
            }
        }
        return undefined;
    }
    createPaperPosition(account, option) {
        if (isNullOrUndefined(account) || isNullOrUndefined(option)) {
            return undefined;
        }
        const paperPosition = this.initializePaperPosition(account, option);
        this._paperPositions.push(paperPosition);
        return paperPosition;
    }
    removePaperPosition(option) {
        const paperPosition = this.getPaperPosition(option);
        this.removePaperPositionFromCache(paperPosition);
        return paperPosition;
    }
    removePaperPositionForOrder(order) {
        const paperPosition = this.getPaperPositionForOrder(order);
        this.removePaperPositionFromCache(paperPosition);
        return paperPosition;
    }
    removePaperPositionForPosition(position) {
        const paperPosition = this.getPaperPositionForPosition(position);
        this.removePaperPositionFromCache(paperPosition);
        return paperPosition;
    }
    changeInstrumentForPaperPosition(oldValue, newValue) {
        if (isNullOrUndefined(oldValue) || isNullOrUndefined(newValue)) {
            return undefined;
        }
        const paperPosition = this.removePaperPosition(oldValue);
        if (isNullOrUndefined(paperPosition)) {
            return undefined;
        }
        paperPosition.Instrument = newValue;
        this._paperPositions.push(paperPosition);
        return paperPosition;
    }
    clear(onlyPositions) {
        if (onlyPositions) {
            const paperPositionsForRemove = this._paperPositions.filter((paperPosition) => paperPosition.isPosition());
            for (let i = 0; i < paperPositionsForRemove.length; i++) {
                const index = this._paperPositions.indexOf(paperPositionsForRemove[i]);
                this._paperPositions.splice(index, 1);
            }
        }
        else {
            this._paperPositions.splice(0, this._paperPositions.length);
        }
    }
    initializePaperPosition(account, option) {
        const paperPosition = new PaperPosition(UUID.generateUUIDv5());
        paperPosition.Analyse = true;
        paperPosition.Active = true;
        paperPosition.Account = account;
        paperPosition.Instrument = option;
        paperPosition.Operation = OperationType.Buy;
        const orderTypes = TradingUtils.getAllowedOrderTypesForTrading(account, option);
        if (!isValidArray(orderTypes) || orderTypes.Contains(OrderType.Limit)) {
            paperPosition.OrderType = OrderType.Limit;
        }
        else {
            paperPosition.OrderType = orderTypes[0];
        }
        const defaultTif = SessionSettings.getDefaultTifForOrderType(paperPosition.OrderType);
        const allowedTifs = TifUtils.getAllowedTifsForTrading(account, option, paperPosition.OrderType);
        paperPosition.TIF = allowedTifs.Contains(defaultTif) ? defaultTif : allowedTifs[0];
        if (paperPosition.TIF === OrderTif.GTD) {
            paperPosition.TIFExpiration = TIF.getDefaultExpireTimeForOrderType(paperPosition.OrderType, option);
        }
        return paperPosition;
    }
    removePaperPositionFromCache(paperPosition) {
        if (isNullOrUndefined(paperPosition)) {
            return;
        }
        const index = this._paperPositions.indexOf(paperPosition);
        this._paperPositions.splice(index, 1);
    }
}
